import { createSlice, isFulfilled, isPending, isRejected, PayloadAction } from '@reduxjs/toolkit';
import { showApiErrors } from '@/utils';
import { initialState, RegionsState, REGIONS_SLICE_NAME } from './models';
import {
  getRegions,
  getPickupLocations,
  getRegionSettlements,
  regionToggleActive,
  settlementToggleActive,
  settlementToggleHeader,
  autoCompleteRegions,
  autoCompleteSettlements,
  createDeliveryWarehouse,
  deleteDeliveryWarehouse,
  getDeliveryWarehouse,
  updateDeliveryWarehouse,
} from './actionCreators';
import { IGetRegionSettlementsResponse, IRegion } from '@/models/regions.model';
import { TGetPickupQuery } from '../helpers/types';

export const slice = createSlice({
  name: REGIONS_SLICE_NAME,
  initialState,
  reducers: {
    resetRegionsState() {
      return { ...initialState };
    },
    changeQuery: (state: RegionsState, action: PayloadAction<TGetPickupQuery>) => {
      state.query = action.payload;
    },
    resetAutocomplete(state) {
      state.autoCompleteRegions = null;
      state.autoCompleteSettlements = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getRegions.fulfilled, (state: RegionsState, action) => {
        state.regions = action.payload;
        state.isLoading = false;
      })
      .addCase(getPickupLocations.fulfilled, (state: RegionsState, action) => {
        state.pickups = action.payload;
        state.isLoading = false;
      })
      .addCase(getRegionSettlements.fulfilled, (state: RegionsState, action) => {
        state.regionsSettlements = action.payload;
        state.isLoading = false;
      })
      .addCase(regionToggleActive.fulfilled, (state: RegionsState, action) => {
        if (!state.regions) return;
        state.regions = {
          ...state.regions,
          data: state?.regions?.data.map((region) =>
            region.id === action.meta.arg ? { ...region, active: !region.active } : region,
          ) as IRegion[],
        };
        state.isLoading = false;
      })
      .addCase(settlementToggleActive.fulfilled, (state: RegionsState, action) => {
        if (!state.regionsSettlements) return;
        state.regionsSettlements = {
          ...state.regionsSettlements,
          data: state?.regionsSettlements?.data.map((settlement) => {
            return settlement.id === action.meta.arg ? { ...settlement, active: !settlement.active } : settlement;
          }),
        } as IGetRegionSettlementsResponse;
        state.isLoading = false;
      })
      .addCase(settlementToggleHeader.fulfilled, (state: RegionsState, action) => {
        if (!state.regionsSettlements) return;
        state.regionsSettlements = {
          ...state.regionsSettlements,
          data: state?.regionsSettlements?.data.map((settlement) => {
            return settlement.id === action.meta.arg ? { ...settlement, in_header: !settlement.in_header } : settlement;
          }),
        } as IGetRegionSettlementsResponse;
        state.isLoading = false;
      })
      .addCase(autoCompleteRegions.fulfilled, (state: RegionsState, action) => {
        state.autoCompleteRegions = action.payload.data;
      })
      .addCase(autoCompleteSettlements.fulfilled, (state: RegionsState, action) => {
        state.autoCompleteSettlements = action.payload.data;
      })
      .addCase(deleteDeliveryWarehouse.fulfilled, (state: RegionsState) => {
        state.isLoading = false;
      })
      .addCase(updateDeliveryWarehouse.fulfilled, (state: RegionsState) => {
        state.isLoading = false;
      })
      .addMatcher(isFulfilled(createDeliveryWarehouse, getDeliveryWarehouse), (state: RegionsState, action) => {
        state.pickup = action.payload.data;
        state.isLoading = false;
      })
      .addMatcher(
        isPending(
          getRegions,
          getPickupLocations,
          createDeliveryWarehouse,
          deleteDeliveryWarehouse,
          updateDeliveryWarehouse,
          getDeliveryWarehouse,
        ),
        (state: RegionsState) => {
          state.isLoading = true;
          state.error = null;
        },
      )
      .addMatcher(
        isRejected(
          getRegions,
          getPickupLocations,
          autoCompleteRegions,
          autoCompleteSettlements,
          createDeliveryWarehouse,
          updateDeliveryWarehouse,
          getDeliveryWarehouse,
          deleteDeliveryWarehouse,
        ),
        (state: RegionsState, action) => {
          const { error } = action;
          state.error = error;
          state.isLoading = false;

          showApiErrors(error);
        },
      );
  },
});

export const { resetRegionsState, changeQuery, resetAutocomplete } = slice.actions;

export default slice.reducer;
